/*
 *  Copyright (C) 2017 Atelier Cartographique <contact@atelier-cartographique.be>
 *
 *  This program is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, version 3 of the License.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

import { findTerm, getAlias, getLang } from '../../app';
import tr, { formatNumber, fromRecord } from '../../locale';
import { markdown } from '../../ports/marked';
import {
    BooleanConfig,
    ConfigWithLabel,
    Feature,
    FeatureViewConfig,
    featureViewDefault,
    HTMLConfig,
    ImageConfig,
    NumberConfig,
    RowConfig,
    StringConfig,
    TermConfig,
    TextConfig,
    URLConfig,
    withLabel,
} from '../../source';
import { A, DIV, IMG, NodeOrOptional } from '../elements';

import piechart from './piechart';

import { catOptions } from 'fp-ts/lib/Array';
import { fromNullable, some } from 'fp-ts/lib/Option';
import { ensureArray, tryBoolean, tryNumber, tryString } from '../../util';
import { renderDefault } from './default';
import {
    elementFactoryCell,
    elementFactoryRow,
    NotNullProperties,
} from './helpers';

const JOIN = '; ';

const renderString = (props: NotNullProperties, row: StringConfig) => {
    const val = props[row.propName];
    return DIV(
        `type-${row.type} level-${row.options.level} style-${row.options.style}`,
        `${val}`,
    );
};

const renderTerm = (props: NotNullProperties, row: TermConfig) =>
    fromNullable(props[row.propName]).map(val =>
        DIV(
            `type-${row.type} level-${row.options.level} style-${row.options.style}`,
            ensureArray(val)
                .map(id =>
                    tryNumber(id)
                        .chain(findTerm)
                        .map<string>(t => fromRecord(t.name))
                        .getOrElse(`${id}`),
                )
                .join(JOIN),
        ),
    );

const addUnit = (unit: string) => (formattedNumber: string) =>
    `${formattedNumber} ${unit}`;

const renderNumber = (props: NotNullProperties, row: NumberConfig) =>
    fromNullable(props[row.propName]).map(val =>
        DIV(
            `type-${row.type} level-${row.options.level} style-${row.options.style}`,
            catOptions(ensureArray(val).map(tryNumber))
                .map(formatNumber)
                .map(addUnit(fromNullable(row.options.unit).getOrElse('')))
                .join(JOIN),
        ),
    );

const renderBoolean = (props: NotNullProperties, row: BooleanConfig) =>
    fromNullable(props[row.propName]).map(val =>
        DIV(
            `type-${row.type} level-${row.options.level} style-${row.options.style}`,
            catOptions(ensureArray(val).map(tryBoolean))
                .map(n => (n ? tr.core('true') : tr.core('false')))
                .join(JOIN),
        ),
    );

const renderUrl = (props: NotNullProperties, row: URLConfig) =>
    fromNullable(props[row.propName]).map(v =>
        DIV(
            `type-${row.type} level-${row.options.level} style-${row.options.style}`,
            ...ensureArray(v).map(e =>
                tryString(e).map(val =>
                    DIV(
                        '',
                        A(
                            {
                                // className: `type-${row.type} level-${row.options.level} style-${row.options.style}`,
                                href: val,
                                target: '_blank',
                            },
                            val,
                        ),
                    ),
                ),
            ),
        ),
    );

const renderImage = (props: NotNullProperties, row: ImageConfig) =>
    fromNullable(props[row.propName])
        .chain(tryString)
        .map(val =>
            IMG({
                className: `type-image`,
                src: val,
                key: val,
            }),
        );

const renderHTML = (props: NotNullProperties, row: HTMLConfig) =>
    tryString(props[row.propName]).map(val =>
        DIV({
            className: `type-html`,
            dangerouslySetInnerHTML: {
                __html: val,
            },
        }),
    );

const renderText = (_props: NotNullProperties, row: TextConfig) => {
    const val = row.options.text;
    return DIV(
        `type-${row.type} level-${row.options.level} style-${row.options.style}`,
        markdown(val),
    );
};

const renderConfiguredRowLabel = (row: ConfigWithLabel, metadataId: string) =>
    DIV(
        { className: 'feature-field-label' },
        getAlias(row.propName, some(metadataId)),
    );

const renderMarkdown = (props: NotNullProperties, row: RowConfig) =>
    fromNullable(props[row.propName]).chain(tryString).map(markdown);

const renderConfiguredRowBody = (
    props: NotNullProperties,
    row: RowConfig,
    asTableRow: boolean,
) => {
    const className = asTableRow
        ? 'feature-field-body as-table-row'
        : 'feature-field-body';
    switch (row.type) {
        case 'string':
            return DIV(className, renderString(props, row));
        case 'markdown':
            return DIV(className, renderMarkdown(props, row));
        case 'number':
            return DIV(className, renderNumber(props, row));
        case 'boolean':
            return DIV(className, renderBoolean(props, row));
        case 'url':
            return DIV(className, renderUrl(props, row));
        case 'image':
            return DIV(className, renderImage(props, row));
        case 'piechart':
            return DIV(className, piechart(props, row));
        case 'html':
            return DIV(className, renderHTML(props, row));
        case 'text':
            return DIV(className, renderText(props, row));
        case 'term':
            return DIV(className, renderTerm(props, row));
    }
};

const renderConfiguredRow = (
    props: NotNullProperties,
    index: number,
    metadataId: string,
    asTableRow: boolean,
    row: RowConfig,
) => {
    const className = 'feature-field';
    const CELL = elementFactoryCell(asTableRow);
    const elements: NodeOrOptional[] = [];
    const key = `feature-field-${row.propName}-${index}`;
    if (!asTableRow && withLabel(row) && row.options.withLabel) {
        elements.push(renderConfiguredRowLabel(row, metadataId));
    }
    elements.push(renderConfiguredRowBody(props, row, asTableRow));

    return CELL({ className, key }, ...elements);
};

const render = (
    feature: Feature,
    config: FeatureViewConfig,
    metadataId: string,
    asTableRow = false,
) => {
    const lc = getLang();
    const ROW = elementFactoryRow(asTableRow);
    if (config && feature) {
        const props = feature.properties;
        const rows = config.rows.filter(r => r.lang === lc);
        if (props && rows.length > 0) {
            return ROW(
                'feature-view config',
                rows.map((r, i) =>
                    renderConfiguredRow(props, i, metadataId, asTableRow, r),
                ),
            );
        }
        if (props && rows.length === 0) {
            return renderDefault(
                feature,
                featureViewDefault,
                metadataId,
                asTableRow,
            );
        }
    }

    return ROW('feature-view config');
};

export default render;
